<template>
  <div class="producttop-slider" :class="{ '-videomode': videoSlideActive }">
    <div class="height-keeper" :style="heightHolder"></div>
    <MediaCarousel
      v-if="!nativeSlider"
      :class="{ '-fullscreen': fullScreen }"
      ref="MediaCarousel"
      :sliderid="'producttop'"
      :proportion="imageSettings.proportion"
      :disableLazy="noLazyImage"
      :href="href"
      :slides="items"
      :swipeActive="true"
      :hideControls="!showControls"
      :transparentVideo="true"
      :invisibleNavBackground="invisibleNavBackground"
      :containToFullHeight="true"
      :largerNav="false"
      :navLargeHitAreas="true"
      :ariaLabel="ariaLabel"
      @on-video-duration="trackVideoDuration"
      @new-active-index="activeIndex = $event"
      @href-clicked="onGotoPageClicked()"
      @image-clicked="clickAction()"
    />

    <ProductNativeSlider
      v-if="nativeSlider"
      ref="nativeSlider"
      :items="items"
      :href="href"
      :swipe="!deactivateMobileSwipe"
      :ariaLabel="ariaLabel"
      @on-video-duration="trackVideoDuration"
      @on-scroll-changed="activeIndex = $event"
      @href-clicked="onGotoPageClicked()"
      @image-clicked="clickAction()"
      @ready="nativeSliderReady()"
      @loading="showNativLoader = $event"
    />
    <SliderControls
      v-if="nativeSlider && items.length > 1 && showControls"
      :slidesLength="items.length"
      :invisibleNavBackground="true"
      :navPlacement="'1px'"
      :loading="showNativLoader"
      :largeHitAreas="true"
      :hideLeft="activeIndex === 0"
      :hideRight="activeIndex === items.length - 1"
      @prev="nativeSliderScroll(-1)"
      @next="nativeSliderScroll(1)"
    />

    <div class="packshot-toggler">
      <div>
        <TextToggler
          v-if="showPackShotToggler && variant.ModelShotPath"
          v-model="viewToggler"
          :options="viewTogglerOptions"
          :small="true"
          :overrideColor="Globals.colors.grayDark"
        />
      </div>
    </div>
    <div v-if="fullScreen" id="fullscreenModalWrapper">
      <portal to="modal-destination">
        <transition name="portal">
          <FullScreenSlider
            v-if="fullScreenMode"
            :title="productName"
            :slides="items"
            :currentIndex="activeIndex"
            @new-active-index="activeIndex = $event"
            @close="closeFulScreen()"
          />
        </transition>
      </portal>
    </div>
  </div>
</template>
<script>
import { gaCustomEvents } from '@/Scripts/lib/tracking/googleAnalyticsTrackerV2';
import productImageGalleryMixin from '@/Scripts/lib/mixins/productImageGalleryMixin.js';

const SliderControls = () => import(/* webpackChunkName: "SliderControls" */ '@/Scripts/components/shared/GenericSlider/Assets/SliderControls.vue');
const ProductNativeSlider = () => import('@/Scripts/components/pages/ProductV2/Blocks/ShopBlockAssets/ProductNativeSlider.vue');
const MediaCarousel = () => import('@/Scripts/components/media/MediaCarousel/MediaCarousel.vue');
const TextToggler = () => import('@/Scripts/components/shared/TextToggler/TextToggler.vue');
const FullScreenSlider = () => import('@/Scripts/components/media/MediaCarousel/FullScreenSlider.vue');

function trapFocus (e, modalId) {
  const isTabPressed = e.key === 'Tab' || e.keyCode === 9;

  if (!isTabPressed) {
    return;
  }
  const focusableElements = 'button, [href], input, select, textarea, iframe, [tabindex]:not([tabindex="-1"])';
  const modal = document.getElementById(modalId);

  // get focusable elements in modal
  const firstFocusableElement = modal.querySelectorAll(focusableElements)[0];
  const focusableContent = modal.querySelectorAll(focusableElements);
  const lastFocusableElement = focusableContent[focusableContent.length - 1];

  if (e.shiftKey) {
    if (document.activeElement === firstFocusableElement) {
      lastFocusableElement.focus();
      e.preventDefault();
    }
  } else if (document.activeElement === lastFocusableElement) {
    firstFocusableElement.focus();
    e.preventDefault();
  }
}

function initTrapFocus (e) {
  return trapFocus(e, 'modalPortalWrapper');
}

function openModalFocus () {
  document.addEventListener('keydown', initTrapFocus);
  document.body.querySelectorAll(':scope > div, :scope > header, :scope > footer, :scope > ul, :scope > section, :scope > a').forEach((el) => {
    if (el !== document.getElementById('modalPortalWrapper')) {
      el.setAttribute('aria-hidden', 'true');
    }
  });
  document.getElementById('modalPortalWrapper').setAttribute('aria-hidden', 'false');
}

export default {
  mixins: [productImageGalleryMixin],

  components: {
    MediaCarousel,
    TextToggler,
    FullScreenSlider,
    ProductNativeSlider,
    SliderControls
  },

  data () {
    return {
      viewToggler: 0,
      showNativLoader: false,
      videoViewIsTracked: false
    };
  },
  watch: {
    activeIndex (newvalue) {
      if (this.colorSliderVariants) {
        if (newvalue > -1 && !isNaN(newvalue)) {
          this.$emit('on-color-index-changed', newvalue);
        }
      }
    },
    variant: {
      deep: true,
      handler () {
        if (this.items) {
          if (this.items.length < this.activeIndex) {
            this.$refs.MediaCarousel.goToIndex(0);
          }
        }
        if ((this.packShotMode && !this.hasPackShots) || (this.videoMode && !this.hasVideo)) {
          this.viewToggler = 0;
        }
      }
    },
    packShotMode () {
      // reset image carusel on mode toggle
      this.$refs.MediaCarousel.goToIndex(0);
    },
    videoMode () {
      // reset image carusel on mode toggle
      this.$refs.MediaCarousel.goToIndex(0);
    }
  },

  computed: {

    viewTogglerOptions () {
      if (!this.hasPackShots && !this.hasVideo) {
        return null;
      }

      const list = [
        { label: this.insertString('Product', 'AllViews'), value: 0, ariaLabel: 'View model images' }
      ];
      if (this.hasPackShots) {
        list.push({ label: this.insertString('Product', 'ProductView'), value: 1, ariaLabel: 'View product images' });
      }
      if (this.hasVideo) {
        list.push({ label: this.insertString('Product', 'VideoView') || 'Video', value: 2, ariaLabel: 'View Video' });
      }
      return list;
    }

  },
  methods: {
    onGotoPageClicked () {
      this.$emit('on-goto-page');
    },
    clickAction () {
      if (this.fullScreen) {
        this.fullScreenMode = true;
        openModalFocus();
      }
    },
    trackVideoDuration (eventLabel) {
      gaCustomEvents('ProductTopSlider', eventLabel, 'video');
    },
    nativeSliderScroll (direction) {
      this.$refs.nativeSlider.$el.scrollTo({
        top: 0,
        left: this.$refs.nativeSlider.$el.children[this.activeIndex + direction].offsetLeft,
        behavior: 'smooth'
      });
    },
    nativeSliderReady () {
      const slideToShow = this.$refs.nativeSlider.$el.children[this.variantIndex];
      if (slideToShow) {
        this.$refs.nativeSlider.$el.scrollTo({
          top: 0,
          left: slideToShow.offsetLeft
        });
      }
    },
    closeFulScreen () {
      this.fullScreenMode = false;
      console.log('close fullscreen');
      setTimeout(() => {
        // focus on the media carousel
        this.$refs.MediaCarousel.$el.focus();
      }, 300);
    }
  }

};
</script>
<style lang="scss">
@import "@/scss/defaults.scss";
.producttop-slider {
  position: sticky !important;
  top: $main-header-height;
  display: grid;
  .go-to-page-link {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100%;
    z-index: 4999;
    pointer-events: all;
  }

  .slider-nav.-next {
    right: 18px;
  }
  .slider-nav.-prev {
    left: 18px;
  }
  .height-keeper {
    padding-bottom: var(--height);
    grid-area: 1 / 1 / 2 / 2;
  }
  .slider-controls,
  .native-slider {
    grid-area: 1 / 1 / 2 / 2;
  }
  .mediaCarousel {
    grid-area: 1 / 1 / 2 / 2;
    &.-fullscreen {
      cursor: zoom-in;
    }
  }

  .-contain {
    height: 100%;
  }
  .packshot-toggler {
    aspect-ratio: 1/1;
    height: 100%;
    left: 50%;
    transform: translateX(-50%);
    position: absolute;
    z-index: 6000;
    pointer-events: none;
    // transition: all 0.33s;
    > div {
      position: absolute;
      bottom: 12px;
      right: 18px;
    }
    .text-toggler {
      pointer-events: all;
    }
  }
  &.-videomode {
    .packshot-toggler {
      aspect-ratio: 4 / 5;
    }
  }
}
</style>
